import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {Box, Button, IconButton, Stack} from "@mui/material";
import {SurveyQuestionOption} from "crqlar-react-common/dist/models/survey/surveyQuestion.model";
import React, {useEffect, useRef} from "react";
import {useTranslation} from "react-i18next";
import {insertOption} from "../../shared/surveyQuestions.util";
import {createEditorOption} from "../surveyEditorQuestions.util";
import {InlineInput, InlineLabelledField, QuestionEditorProps} from "./questionEditor.types";

function ChecklistQuestionEditor(props: QuestionEditorProps) {
    const {t} = useTranslation();
    const {question, patch, language, disabled} = props;

    /*
        ADD ITEM left the caret in the item above it, so the next thing typed was appended to the
        previous label and the new row stayed empty. The row does not exist yet when the button is
        pressed - the parent re-renders with it - so the id is parked here and claimed by the
        effect once that row's input has mounted.
    */
    const pendingFocusRef = useRef<string | undefined>(undefined);
    const optionInputsRef = useRef<Record<string, HTMLInputElement | null>>({});

    useEffect(() => {
        const optionId = pendingFocusRef.current;
        if (!optionId) return;

        const input = optionInputsRef.current[optionId];
        if (!input) return;

        pendingFocusRef.current = undefined;
        input.focus();
    });

    const addItem = () => {
        const option = createEditorOption();
        pendingFocusRef.current = option.id;

        patch({options: insertOption(question.options ?? [], option)});
    };

    const patchOption = (optionId: string, partial: Partial<SurveyQuestionOption>) => patch({
        options: (question.options ?? []).map(option => (
            option.id === optionId ? {...option, ...partial} : option
        )),
    });

    return (
        <Stack gap={1.5}>
            <InlineLabelledField label={t('Items')}>
                <Stack gap={1} sx={{width: '100%'}}>
                    {(question.options ?? []).map(option => (
                        <Stack key={option.id} direction={'row'} alignItems={'center'} gap={1}>
                            <Box sx={{flex: 1, minWidth: 0}}>
                                <InlineInput
                                    value={option.label?.[language] ?? ''}
                                    placeholder={t('Something the guest confirms')}
                                    inputRef={(input: HTMLInputElement | null) => {
                                        if (option.id) optionInputsRef.current[option.id] = input;
                                    }}
                                    onChange={(value) => patchOption(option.id!, {
                                        label: {...option.label, [language]: value},
                                    })}
                                    disabled={disabled}
                                />
                            </Box>

                            <IconButton
                                size={'small'}
                                disabled={disabled}
                                onClick={() => patch({
                                    options: (question.options ?? []).filter(entry => entry.id !== option.id)
                                })}
                            >
                                <DeleteOutlineIcon fontSize={'small'}/>
                            </IconButton>
                        </Stack>
                    ))}

                    <Button
                        size={'small'}
                        startIcon={<AddIcon/>}
                        disabled={disabled}
                        sx={{alignSelf: 'flex-start'}}
                        onClick={addItem}
                    >
                        {t('Add item')}
                    </Button>
                </Stack>
            </InlineLabelledField>
        </Stack>
    );
}

export default ChecklistQuestionEditor;
